import axios from 'axios';

import api from './api';
import { API_BASE_URL } from '../config/constants';

// Create axios instance with base URL
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const jobService = {
  async getJobs(params) {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const queryString = new URLSearchParams({
        ...params,
        userId: user.id
      }).toString();


      const response = await apiClient.get(`/jobs?${queryString}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async getJobTitle(id) {
    try {
      const response = await api.get(`/jobs/${id}/title`);
      console.log('Job title response:', response.data.data);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  async getJob(id) {
    try {
      const response = await api.get(`/jobs/${id}`);
      // console.log('Get job response:', response.data);

      if (!response.data?.data) {
        throw new Error('Invalid response format');
      }

      // Extract thread_id and assistant_id from the response
      const jobData = response.data.data;
      return {
        data: {
          ...jobData,
          threadId: jobData.threadId,
          assistantId: jobData.assistantId
        }
      };
    } catch (error) {
      console.error('Get job error:', error);
      throw error;
    }
  },

  async createJob(jobData) {
    try {
      const payload = {
        ...jobData,
        jobTitle: jobData.jobTitle || jobData.title
      };

      const response = await api.post('/jobs', payload);

      // Log the response for debugging
      console.log('Create job response:', response.data);

      // Ensure we have the correct data structure
      if (!response.data?.data) {
        throw new Error('Invalid response format');
      }

      return {
        data: response.data.data // Return the nested data object
      };
    } catch (error) {
      console.error('Create job error:', error);
      throw error;
    }
  },

  async updateJob(id, jobData) {
    const response = await apiClient.patch(`/jobs/${id}`, jobData);
    return response.data;
  },

  async deleteJob(id) {
    const response = await apiClient.delete(`/jobs/${id}`);
    return response.data;
  },

  async generateContent(id, platform) {
    const response = await apiClient.post(`/jobs/${id}/generate-content`, { platform });
    return response.data;
  },

  // Removed generateScreeningQuestions - static prescreening functionality removed

  async generatePsychoanalyticalQuestions(id, difficulty) {
    const response = await apiClient.post(`/jobs/${id}/psychoanalytical/questions`, {
      difficulty
    });
    return response.data;
  },

  async generateFinalInterviewQuestions(id, difficulty) {
    const response = await apiClient.post(`/jobs/${id}/final-interview/questions`, {
      difficulty
    });
    return response;
  },


  // In jobService.js

  // Get screening questions
  // Removed getScreeningQuestions and deleteScreeningQuestion - static prescreening functionality removed


  async getJobCandidates(id, params) {
    const response = await apiClient.get(`/jobs/${id}/candidates`, { params });
    return response.data;
  },
  async updateJobDescription(id, descriptionData) {
    const response = await apiClient.patch(`/jobs/${id}/description`, {
      generatedContent: descriptionData
    });
    return response.data;
  },

  async getMediaContent(id) {
    try {
      const response = await apiClient.get(`/jobs/media-plan/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get media content error:', error);
      throw error;
    }
  },

  async updateMediaContent(id, platform, content) {
    try {
      const response = await apiClient.patch(`/jobs/media-plan/${id}`, {
        platform,
        content
      });
      return response.data;
    } catch (error) {
      console.error('Update media content error:', error);
      throw error;
    }
  },

  async generateMediaContent(id, platform, jobDescription, userId, retryCount = 0) {
    try {
      const job = await this.getJob(id);

      const response = await api.post(`/ai/jobs/${id}/media/generate`, {
        platform,
        prompt: jobDescription,
        user_id: userId,
        agent_name: 'create-media-plan'
      });

      console.log('Media content response:', response.data);
      console.log('Response data structure:', {
        hasData: !!response.data?.data,
        dataType: typeof response.data?.data,
        hasOptions: !!response.data?.data?.options,
        hasContent: !!response.data?.data?.content,
        hasNestedData: !!response.data?.data?.data,
        responseKeys: Object.keys(response.data || {}),
        dataKeys: Object.keys(response.data?.data || {})
      });

      // Check for error response
      if (response.data?.error) {
        // Handle retryable errors
        if ((response.data.code === 'SERVICE_UNAVAILABLE' || response.data.code === 'RATE_LIMIT_EXCEEDED') && retryCount < 2) {
          console.log(`Retrying due to ${response.data.code}. Attempt ${retryCount + 1}/3`);
          const retryDelay = response.data.retryAfter || 30;
          await new Promise(resolve => setTimeout(resolve, retryDelay * 1000));
          return this.generateMediaContent(id, platform, jobDescription, userId, retryCount + 1);
        }
        
        throw new Error(response.data.message || 'Failed to generate media content');
      }

      // Handle the new response format with options array (from Gemini service)
      // The response is wrapped by apiResponse, so we need to access response.data.data
      if (response.data?.data?.data?.options && Array.isArray(response.data.data.data.options)) {
        console.log('Handling nested options array format');
        return {
          options: response.data.data.data.options,
          suggestedHashtags: response.data.data.data.suggestedHashtags || [],
          suggestedKeywords: response.data.data.data.suggestedKeywords || []
        };
      }
      
      // Handle the response format with options array (direct access)
      if (response.data?.data?.options && Array.isArray(response.data.data.options)) {
        console.log('Handling options array format');
        return {
          options: response.data.data.options,
          suggestedHashtags: response.data.data.suggestedHashtags || [],
          suggestedKeywords: response.data.data.suggestedKeywords || []
        };
      }

      // Handle legacy content format
      if (response.data?.data?.content) {
        console.log('Handling legacy content format');
        return {
          content: response.data.data.content,
          suggestedHashtags: response.data.data.suggestedHashtags || [],
          suggestedKeywords: response.data.data.suggestedKeywords || []
        };
      }

      // Handle response with data directly at root level
      if (response.data?.options && Array.isArray(response.data.options)) {
        console.log('Handling root-level options format');
        return {
          options: response.data.options,
          suggestedHashtags: response.data.suggestedHashtags || [],
          suggestedKeywords: response.data.suggestedKeywords || []
        };
      }

      // Handle response with content directly at root level
      if (response.data?.content) {
        console.log('Handling root-level content format');
        return {
          content: response.data.content,
          suggestedHashtags: response.data.suggestedHashtags || [],
          suggestedKeywords: response.data.suggestedKeywords || []
        };
      }

      // If we get here, log the full response for debugging
      console.error('Unrecognized response format:', response.data);
      console.error('Response structure:', JSON.stringify(response.data, null, 2));
      
      throw new Error('Invalid response format from AI service');
    } catch (error) {
      console.error('Generate media content error:', error);
      throw error;
    }
  },

  saveScreeningQuestions: async (jobId, data) => {
    try {
      const response = await axios.post(
        `/api/jobs/${jobId}/prescreening/questions`,
        data
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },



  // Interview related methods
  async getPreScreenedCandidates(jobId) {
    try {
      const response = await apiClient.get(`/jobs/${jobId}/candidates/prescreened`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async generateInterviewQuestions(jobId, candidateIds) {
    try {
      const response = await apiClient.post(`/jobs/${jobId}/interviews/questions`, {
        candidateIds
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async updateInterviewQuestion(jobId, questionId, updatedQuestion) {
    try {
      const response = await apiClient.patch(`/jobs/${jobId}/interviews/questions/${questionId}`, {
        question: updatedQuestion
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Offer letter related methods
  async getFinalizedCandidates(jobId) {
    try {
      const response = await apiClient.get(`/jobs/${jobId}/candidates/finalized`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },



  async updateOfferLetter(jobId, offerId, updates) {
    try {
      const response = await apiClient.patch(`/jobs/${jobId}/offers/${offerId}`, updates);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async generateJobDescription(jobData) {
    try {
      if (!jobData._id) {
        throw new Error('Job ID is required for generating description');
      }

      const response = await api.post(`/ai/jobs/${jobData._id}/description/generate`, {
        title: jobData.title || jobData.jobTitle,
        department: jobData.department,
        qualifications: jobData.qualifications,
        responsibilities: jobData.responsibilities,
        prompt: jobData.prompt
      });

      // Extract thread_id and assistant_id from the AI response
      const aiResponse = response.data;
      if (aiResponse.thread_id && aiResponse.assistant_id) {
        return {
          ...aiResponse,
          data: {
            ...aiResponse.data,
            thread_id: aiResponse.thread_id,
            assistant_id: aiResponse.assistant_id
          }
        };
      }

      return response.data;
    } catch (error) {
      console.error('Generate job description error:', error);
      throw error;
    }
  },

  async processResumes(jobId, files) {
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await api.post(`/ai/jobs/${jobId}/resumes/process`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Process resumes error:', error);
      throw error;
    }
  },


  async continueJobDescription(jobId, data = {}) {
    try {
      // Get user info from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || user._id;

      if (!userId) {
        throw new Error('User ID not found. Please log in again.');
      }

      const payload = {
        user_id: userId
      };

      // Add user_message if provided (optional)
      if (data.user_message) {
        payload.user_message = data.user_message;
      }

      // Add existing content if available (optional)
      if (data.existing_content) {
        payload.existing_content = data.existing_content;
      }

      console.log('Continue payload:', payload);

      // Call the correct endpoint for job description continue
      const response = await api.post(`/ai/jobs/${jobId}/description/continue`, payload);
      console.log('Continue response:', response.data);
      
      // The response is wrapped by apiResponse, so we need to access response.data.data
      const responseData = response.data.data;
      console.log('Response data:', responseData);
      
      // Handle the response structure consistently
      if (responseData?.content?.content) {
        // If we have nested content structure
        return {
          content: responseData.content,
          thread_id: responseData.thread_id,
          assistant_id: responseData.assistant_id
        };
      } else if (responseData?.content) {
        // If we have direct content structure
        return {
          content: responseData.content,
          thread_id: responseData.thread_id,
          assistant_id: responseData.assistant_id
        };
      } else {
        // Fallback to full response
        return responseData;
      }
    } catch (error) {
      console.error('Continue job description error:', error);
      throw error;
    }
  },

  async continueMediaPlan(jobId, platform, data = {}) {
    try {
      // Call the dedicated enhance endpoint which passes existing_content to the AI
      const response = await api.post(`/ai/jobs/${jobId}/media/enhance`, {
        platform,
        user_message: data.user_message || 'Please improve this content',
        existing_content: data.existing_content,
      });

      // Server returns { success: true, data: { content, suggestedHashtags, suggestedKeywords, thread_id, assistant_id } }
      const responseData = response.data?.data;
      const content = responseData?.content || null;

      return {
        data: { content, suggestedHashtags: responseData?.suggestedHashtags, suggestedKeywords: responseData?.suggestedKeywords },
        thread_id: responseData?.thread_id,
        assistant_id: responseData?.assistant_id,
      };
    } catch (error) {
      console.error('Continue media plan error:', error);
      throw error;
    }
  },

  async continuePreScreening(jobId, inputContent) {
    const response = await api.post(`/ai/jobs/${jobId}/screening/continue`, {
      inputContent
    });
    return response.data;
  },

  getJob: async (jobId) => {
    try {
      const response = await api.get(`/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching job:', error);
      throw error;
    }
  },

  updatePassingScore: async (jobId, data) => {
    try {
      const response = await api.patch(`/jobs/${jobId}/passing-score`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating passing score:', error);
      throw error;
    }
  }
};

export default jobService;